import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { clearListSearchFilter } from 'Store/lists/actions';
import { getListById } from 'Store/reducers';

import InputGroup from 'Components/InputGroup';
import InputAction from 'Components/InputAction';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';

class ListFilterInput extends React.Component {
  constructor(props) {
    super(props);

    // The canonical value is in the Lists reducer,
    // but we keep it here so we can still use a timeout
    // to avoid unnecessary list renders.
    this.state = {
      value: '',
      filtering: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.changeTimeout = undefined;
  }

  componentDidUpdate(prevProps) {
    // This is used on the users page, which has tabs.
    // So we clear the filter when the List ID changes.
    if (
      this.props.listId !== prevProps.listId &&
      prevProps.listId !== undefined
    ) {
      this.clearFilter(this.props.listId);
    }

    if (
      prevProps.searchFilterString !== this.props.searchFilterString &&
      this.props.searchFilterString !== this.state.value &&
      !this.filtering
    ) {
      const value =
        this.props.searchFilterString === null
          ? ''
          : this.props.searchFilterString;
      this.setState({ value });
    }

    // If the user has scrolled down in the user list, filters,
    // and the result list is long, we should make sure to send
    // them to the top of the page.
    if (
      this.props.listId === prevProps.listId &&
      this.props.filteredIds !== prevProps.filteredIds &&
      Array.isArray(this.props.filteredIds)
    ) {
      window.scrollTo(0, 0);
    }
  }

  getInputAction() {
    if (this.state.filtering)
      return <InputAction clickHandler={() => null} type="loading" />;
    return this.state.value.length ? (
      <InputAction
        clickHandler={() => this.clearFilter(this.props.listId)}
        type="clear"
      />
    ) : (
      undefined
    );
  }

  clearFilter(listId) {
    this.setState({ value: '' }, () =>
      this.props.clearListSearchFilter(listId)
    );
  }

  handleUpdate(value) {
    this.setState({ filtering: false }, () =>
      this.props.updateFilterCallback(value, this.props.listId)
    );
  }

  handleChange(target) {
    if (this.changeTimeout) window.clearTimeout(this.changeTimeout);
    this.setState({ value: target.value, filtering: true }, () => {
      this.changeTimeout = window.setTimeout(
        this.handleUpdate,
        200,
        target.value
      );
    });
  }

  render() {
    const { filteredIds, showNoteWithZeroResults } = this.props;
    const { value } = this.state;
    let resultCount = filteredIds ? filteredIds.length : null;
    if (showNoteWithZeroResults && !filteredIds) resultCount = 0;
    const note =
      resultCount !== null ? (
        <ConnectedFormattedMessage
          id="form.field.searchFilter.note"
          values={{ result_count: resultCount }}
        />
      ) : null;

    return (
      <InputGroup
        type="search"
        fieldName="searchFilter"
        fieldSize="small"
        name="searchFilter"
        placeholderId="form.field.search.placeholder"
        value={value}
        handleChange={this.handleChange}
        inputAction={this.getInputAction()}
        note={note}
      />
    );
  }
}

ListFilterInput.propTypes = {
  filteredIds: PropTypes.array,
  searchFilterString: PropTypes.string,
  listId: PropTypes.string.isRequired,
  clearListSearchFilter: PropTypes.func.isRequired,
  updateFilterCallback: PropTypes.func.isRequired,
  showNoteWithZeroResults: PropTypes.bool,
};

ListFilterInput.defaultProps = {
  showNoteWithZeroResults: false,
};

const mapStateToProps = (state, { listId }) => {
  const list = getListById(state, listId);
  const result = {};
  if (list) {
    result.filteredIds = list.filteredIds;
    result.searchFilterString = list.searchFilterString;
  }
  return result;
};

export default connect(mapStateToProps, {
  clearListSearchFilter,
})(ListFilterInput);
